import { isSameDay } from "date-fns"
import type { TFunction } from "i18next"
import type { AppContextState } from "../../types/app-context"

type TableHeaderTextOptions = {
  appContext: AppContextState
  fromDate: Date
  toDate: Date
  selectedMembershipName?: string
  formatDate: (date: Date, format: string) => string
  t: TFunction
}

type HourReportsHeaderTextOptions = {
  fromDate: Date
  toDate: Date
  isUserContext: boolean
  membershipName: string
  formatDate: (date: Date, format: string) => string
  t: TFunction
}

const DATE_FORMAT = "dd MMM"

export const getTableHeaderText = ({
  appContext,
  fromDate,
  toDate,
  selectedMembershipName,
  formatDate,
  t,
}: TableHeaderTextOptions) => {
  const { subContext, mainContext } = appContext
  const formattedFromDate = formatDate(fromDate, DATE_FORMAT)
  const dateText = isSameDay(fromDate, toDate)
    ? t("header.on", { defaultValue: "on {{from}}", from: formattedFromDate })
    : t("header.between", {
        defaultValue: "between {{from}} - {{to}}",
        from: formattedFromDate,
        to: formatDate(toDate, DATE_FORMAT),
      })

  if (selectedMembershipName)
    return t("header.member", {
      defaultValue: "Reported hours for {{name}} {{dateText}}",
      name: selectedMembershipName,
      dateText,
    })
  if (subContext?.type === "customer")
    return t("header.customer", {
      defaultValue: "Reported hours on all projects for customer {{name}} {{dateText}}",
      name: subContext.name,
      dateText,
    })
  if (subContext?.type === "workspace")
    return t("header.workspace", {
      defaultValue: "Reported hours for all members of workspace {{name}} {{dateText}}",
      name: subContext.name,
      dateText,
    })
  if (subContext?.type === "member")
    return t("header.member", {
      defaultValue: "Reported hours for {{name}} {{dateText}}",
      name: subContext.name,
      dateText,
    })
  if (mainContext?.type === "org")
    return t("header.allOrg", { defaultValue: "All reported hours of this organization {{dateText}}", dateText })
  return t("header.personal", { defaultValue: "Personal reported hours {{dateText}}", dateText })
}

export const getHourReportsHeaderText = ({
  fromDate,
  toDate,
  isUserContext,
  membershipName,
  formatDate,
  t,
}: HourReportsHeaderTextOptions) => {
  const formattedFromDate = formatDate(fromDate, "dd MMMM yyyy")
  const dateText = isSameDay(fromDate, toDate)
    ? t("header.on", { defaultValue: "on {{from}}", from: formattedFromDate })
    : t("header.between", {
        defaultValue: "between {{from}} - {{to}}",
        from: formattedFromDate,
        to: formatDate(toDate, "dd MMMM yyyy"),
      })
  if (membershipName)
    return t("header.reportFor", {
      defaultValue: "Report hours for {{name}} {{dateText}}",
      name: membershipName,
      dateText,
    })
  return isUserContext
    ? t("header.reportYours", { defaultValue: "Report your hours {{dateText}}", dateText })
    : t("header.report", { defaultValue: "Report hours {{dateText}}", dateText })
}
